import React, { createContext, useContext, ReactNode, useState } from 'react';
import { useOrders as useOrdersHook, OrderResponse, PaginatedResponse } from '../hooks/useOrders';
import { useAuth } from './AuthContext';

// Define filter types
export interface OrderFilters {
  search?: string;
  order_status?: string;
  payment_status?: string;
  shipment_status?: string;
  sort_date?: 'asc' | 'desc';
}

interface OrderContextType {
  // Pagination state
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  
  // Filters state
  filters: OrderFilters;
  setFilters: (filters: OrderFilters) => void;
  
  // Order loading states
  ordersQuery: {
    data?: PaginatedResponse<OrderResponse>;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    refetch: () => void;
  };
  
  // Order operations
  cancelOrder: (orderId: string | number) => Promise<any>;
  isCancelingOrder: boolean;
  cancelOrderError: Error | null;
  
  // Label operations
  downloadLabel: (orderId: string | number) => Promise<any>;
  isDownloadingLabel: boolean;
  downloadLabelError: Error | null;
  
  // Single order operations
  getOrderById: (orderId: string | number) => Promise<OrderResponse>;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const OrderProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  // Get authentication state
  const { isAuthenticated } = useAuth();
  
  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  
  // Filters state
  const [filters, setFilters] = useState<OrderFilters>({
    sort_date: 'desc'
  });
  
  // Get order operations from hook
  const ordersHook = useOrdersHook();
  
  // Query for paginated orders with filters - only when authenticated
  const ordersQuery = ordersHook.useOrdersQuery(currentPage, pageSize, filters, isAuthenticated);
  
  return (
    <OrderContext.Provider 
      value={{
        // Pagination
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        
        // Filters
        filters,
        setFilters,
        
        // Orders query
        ordersQuery: {
          data: ordersQuery.data,
          isLoading: ordersQuery.isLoading,
          isError: ordersQuery.isError,
          error: ordersQuery.error as Error | null,
          refetch: ordersQuery.refetch,
        },
        
        // Order operations
        cancelOrder: ordersHook.cancelOrder,
        isCancelingOrder: ordersHook.isCancelingOrder,
        cancelOrderError: ordersHook.cancelOrderError as Error | null,
        
        // Label operations
        downloadLabel: ordersHook.downloadLabel,
        isDownloadingLabel: ordersHook.isDownloadingLabel,
        downloadLabelError: ordersHook.downloadLabelError as Error | null,
        
        // Single order operations
        getOrderById: ordersHook.getOrderById,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrders = (): OrderContextType => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};